"use client";

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import cnBind from "classnames/bind";
import { useTranslation } from "react-i18next";

import { ArrowDownIcon } from "@/shared/assests/svg/svg";
import {
    INDUSTRY_PRESETS,
    type IndustryPreset,
} from "@/shared/types/industryPreset";

import styles from "./IndustryPresetSelect.module.scss";

const cx = cnBind.bind(styles);

type IndustryPresetSelectProps = {
    value: IndustryPreset | "";
    onChange: (value: IndustryPreset) => void;
    onBlur?: () => void;
    placeholder?: string;
    invalid?: boolean;
    disabled?: boolean;
    className?: string;
};

export function IndustryPresetSelect({
    value,
    onChange,
    onBlur,
    placeholder,
    invalid = false,
    disabled = false,
    className,
}: IndustryPresetSelectProps) {
    const { t } = useTranslation("common");
    const resolvedPlaceholder = placeholder ?? t("home.modalCallBack.industryPlaceholder");
    const options = useMemo(
        () =>
            INDUSTRY_PRESETS.map((preset) => ({
                value: preset,
                label: t(`home.industryPresets.${preset}`),
            })),
        [t],
    );
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const rootRef = useRef<HTMLDivElement>(null);
    const listboxId = useId();

    const selectedOption = options.find((option) => option.value === value);
    const displayLabel = selectedOption?.label ?? resolvedPlaceholder;

    const close = useCallback(() => {
        setIsOpen(false);
        setHighlightedIndex(-1);
        onBlur?.();
    }, [onBlur]);

    const open = useCallback(() => {
        if (disabled) {
            return;
        }
        setIsOpen(true);
        const selectedIndex = options.findIndex((option) => option.value === value);
        setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }, [disabled, options, value]);

    const selectOption = useCallback(
        (preset: IndustryPreset) => {
            onChange(preset);
            close();
        },
        [close, onChange],
    );

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handlePointerDown = (event: MouseEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) {
                close();
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                close();
                return;
            }

            if (event.key === "ArrowDown") {
                event.preventDefault();
                setHighlightedIndex((current) =>
                    current >= options.length - 1 ? 0 : current + 1,
                );
                return;
            }

            if (event.key === "ArrowUp") {
                event.preventDefault();
                setHighlightedIndex((current) =>
                    current <= 0 ? options.length - 1 : current - 1,
                );
                return;
            }

            if (event.key === "Enter" && highlightedIndex >= 0) {
                event.preventDefault();
                selectOption(options[highlightedIndex].value);
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [close, highlightedIndex, isOpen, options, selectOption]);

    return (
        <div ref={rootRef} className={cx("root", className)}>
            <button
                type="button"
                className={cx("trigger", "glass-input-fill", {
                    isOpen,
                    isPlaceholder: !selectedOption,
                    invalid,
                })}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls={listboxId}
                disabled={disabled}
                onClick={() => (isOpen ? close() : open())}
            >
                <span className={cx("triggerLabel")}>{displayLabel}</span>
                <ArrowDownIcon className={cx("chevron", { isOpen })} aria-hidden />
            </button>

            {isOpen ? (
                <ul id={listboxId} role="listbox" className={cx("panel")} aria-label={resolvedPlaceholder}>
                    {options.map((option, index) => (
                        <li key={option.value} role="presentation">
                            <button
                                type="button"
                                role="option"
                                aria-selected={value === option.value}
                                className={cx("option", {
                                    isSelected: value === option.value,
                                    isHighlighted: highlightedIndex === index,
                                })}
                                onMouseEnter={() => setHighlightedIndex(index)}
                                onClick={() => selectOption(option.value)}
                            >
                                {option.label}
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}
